import { amber, cyan, deepPurple, yellow } from "@mui/material/colors";
import {
  formatPounds,
  LQG_THRESHOLD_HAZARDOUS_KG,
  thresholdPounds,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";
import { useCallback, useMemo } from "react";

import { getWasteThresholdsForState, WasteThreshold } from "../../wasteThresholds";

export type ThresholdWithDisplay = WasteThreshold & {
  id: string;
  color: string;
  displayValue: string;
};

export function useWasteThresholds(facilityState?: string | null) {
  // Get thresholds for the facility's state
  const thresholds = useMemo(() => {
    return getWasteThresholdsForState(facilityState || "");
  }, [facilityState]);

  // Create threshold ID for consistent referencing
  const getThresholdId = useCallback((threshold: WasteThreshold) => {
    return `${threshold.type}-${threshold.threshold}-${threshold.acute ? "acute" : "regular"}`;
  }, []);

  // Get color for a threshold based on its type and properties
  const getThresholdColor = useCallback((threshold: WasteThreshold) => {
    if (threshold.acute) {
      // Acute thresholds use deep purple
      return deepPurple[400];
    }

    // Non-acute LQG-level thresholds share the federal color, so a state
    // analog at the same value (e.g. California LQG at 1,000 kg) renders as
    // one line with the federal LQG instead of two staggered lines
    if (threshold.threshold >= thresholdPounds(LQG_THRESHOLD_HAZARDOUS_KG)) {
      return cyan[700];
    }
    return threshold.type === "federal" ? amber[700] : yellow[700];
  }, []);

  // Format threshold value for display
  const formatThresholdValue = useCallback((value: number) => `${formatPounds(value)} lbs`, []);

  // Create enhanced threshold objects with display properties (excluding zero thresholds)
  const thresholdsWithDisplay: ThresholdWithDisplay[] = useMemo(() => {
    return thresholds
      .filter((threshold) => threshold.threshold > 0)
      .map((threshold) => ({
        ...threshold,
        id: getThresholdId(threshold),
        color: getThresholdColor(threshold),
        displayValue: formatThresholdValue(threshold.threshold),
      }));
  }, [thresholds, getThresholdId, getThresholdColor, formatThresholdValue]);

  // Initialize default threshold visibility (all visible by default)
  const defaultThresholdVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    thresholdsWithDisplay.forEach((threshold) => {
      visibility[threshold.id] = true;
    });
    return visibility;
  }, [thresholdsWithDisplay]);

  return {
    thresholds: thresholdsWithDisplay,
    defaultThresholdVisibility,
    getThresholdId,
    getThresholdColor,
    formatThresholdValue,
  };
}
